import * as XLSX from "xlsx";

/**
 * Exporta datos a un archivo Excel
 * @param {Array} datos - Array de objetos con los datos a exportar
 * @param {string} nombreArchivo - Nombre del archivo (sin extensión)
 * @param {string} nombreHoja - Nombre de la hoja
 * @param {object} metadatos - Metadatos opcionales (título, filtros aplicados, fecha)
 */
export const exportarAExcel = (datos, nombreArchivo, nombreHoja = "Reporte", metadatos = {}) => {
  try {
    // Crear un nuevo libro de trabajo
    const wb = XLSX.utils.book_new();

    // Preparar datos con metadatos si existen
    let datosParaExportar = [];

    // Agregar título si existe
    if (metadatos.titulo) {
      datosParaExportar.push({ A: metadatos.titulo });
      datosParaExportar.push({}); // Línea vacía
    }

    // Agregar fecha de generación
    const fechaGeneracion = new Date().toLocaleString("es-CO", {
      dateStyle: "medium",
      timeStyle: "short",
    });
    datosParaExportar.push({ A: `Fecha de generación: ${fechaGeneracion}` });

    // Agregar filtros aplicados si existen
    if (metadatos.filtros && Object.keys(metadatos.filtros).length > 0) {
      datosParaExportar.push({ A: "Filtros aplicados:" });
      Object.entries(metadatos.filtros).forEach(([key, value]) => {
        if (value) {
          datosParaExportar.push({ A: `  ${key}: ${value}` });
        }
      });
    }

    datosParaExportar.push({}); // Línea vacía
    datosParaExportar.push({}); // Línea vacía

    // Crear la hoja de cálculo con los datos
    const ws = XLSX.utils.json_to_sheet(datos, {
      origin: datosParaExportar.length > 0 ? datosParaExportar.length : 0,
    });

    // Agregar metadatos al inicio si existen
    if (datosParaExportar.length > 0) {
      XLSX.utils.sheet_add_json(ws, datosParaExportar, {
        skipHeader: true,
        origin: 0,
      });
    }

    // Ajustar ancho de columnas automáticamente
    const range = XLSX.utils.decode_range(ws["!ref"]);
    const columnWidths = [];

    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 10;
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[cellAddress];
        if (cell && cell.v) {
          const cellValue = cell.v.toString();
          maxWidth = Math.max(maxWidth, cellValue.length);
        }
      }
      columnWidths.push({ wch: Math.min(maxWidth + 2, 50) });
    }

    ws["!cols"] = columnWidths;

    // Agregar la hoja al libro
    XLSX.utils.book_append_sheet(wb, ws, nombreHoja);

    // Generar el archivo y descargarlo
    XLSX.writeFile(wb, `${nombreArchivo}.xlsx`);

    return true;
  } catch (error) {
    console.error("Error al exportar a Excel:", error);
    return false;
  }
};

/**
 * Exporta múltiples hojas de datos a un archivo Excel
 * @param {Array} hojas - Array de objetos { nombre, datos, metadatos }
 * @param {string} nombreArchivo - Nombre del archivo (sin extensión)
 */
export const exportarMultiplesHojasAExcel = (hojas, nombreArchivo) => {
  try {
    const wb = XLSX.utils.book_new();

    hojas.forEach((hoja) => {
      const { nombre, datos, metadatos = {} } = hoja;

      let datosParaExportar = [];

      if (metadatos.titulo) {
        datosParaExportar.push({ A: metadatos.titulo });
        datosParaExportar.push({});
      }

      const fechaGeneracion = new Date().toLocaleString("es-CO", {
        dateStyle: "medium",
        timeStyle: "short",
      });
      datosParaExportar.push({ A: `Fecha de generación: ${fechaGeneracion}` });

      if (metadatos.filtros && Object.keys(metadatos.filtros).length > 0) {
        datosParaExportar.push({ A: "Filtros aplicados:" });
        Object.entries(metadatos.filtros).forEach(([key, value]) => {
          if (value) {
            datosParaExportar.push({ A: `  ${key}: ${value}` });
          }
        });
      }

      datosParaExportar.push({});
      datosParaExportar.push({});

      const ws = XLSX.utils.json_to_sheet(datos, {
        origin: datosParaExportar.length > 0 ? datosParaExportar.length : 0,
      });

      if (datosParaExportar.length > 0) {
        XLSX.utils.sheet_add_json(ws, datosParaExportar, {
          skipHeader: true,
          origin: 0,
        });
      }

      // Ajustar ancho de columnas
      const range = XLSX.utils.decode_range(ws["!ref"]);
      const columnWidths = [];

      for (let C = range.s.c; C <= range.e.c; ++C) {
        let maxWidth = 10;
        for (let R = range.s.r; R <= range.e.r; ++R) {
          const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
          const cell = ws[cellAddress];
          if (cell && cell.v) {
            const cellValue = cell.v.toString();
            maxWidth = Math.max(maxWidth, cellValue.length);
          }
        }
        columnWidths.push({ wch: Math.min(maxWidth + 2, 50) });
      }

      ws["!cols"] = columnWidths;

      XLSX.utils.book_append_sheet(wb, ws, nombre);
    });

    XLSX.writeFile(wb, `${nombreArchivo}.xlsx`);

    return true;
  } catch (error) {
    console.error("Error al exportar múltiples hojas a Excel:", error);
    return false;
  }
};

/**
 * Prepara datos para exportación formateando valores
 * @param {Array} datos - Array de objetos
 * @param {object} formatoColumnas - Objeto con formato por columna
 * @returns {Array} Datos formateados
 */
export const prepararDatosParaExportacion = (datos, formatoColumnas = {}) => {
  return datos.map((fila) => {
    const filaFormateada = { ...fila };

    Object.entries(formatoColumnas).forEach(([columna, formato]) => {
      if (filaFormateada[columna] !== undefined && filaFormateada[columna] !== null) {
        switch (formato) {
          case "moneda":
            filaFormateada[columna] = new Intl.NumberFormat("es-CO", {
              style: "currency",
              currency: "COP",
              minimumFractionDigits: 0,
            }).format(filaFormateada[columna]);
            break;
          case "numero":
            filaFormateada[columna] = new Intl.NumberFormat("es-CO").format(
              filaFormateada[columna]
            );
            break;
          case "porcentaje":
            filaFormateada[columna] = `${filaFormateada[columna]}%`;
            break;
          case "fecha":
            if (filaFormateada[columna] instanceof Date) {
              filaFormateada[columna] = filaFormateada[columna].toLocaleDateString("es-CO");
            }
            break;
          case "boolean":
            filaFormateada[columna] = filaFormateada[columna] ? "Sí" : "No";
            break;
          default:
            break;
        }
      }
    });

    return filaFormateada;
  });
};
